// Turns the flat synced-contacts list into something CRM-shaped: who each
// person actually is to the business, what they're attached to, and when you
// last had something happen with them.
//
// Contacts, deals and leads are all linked by `contactId`, so this is an exact
// join — no name or phone guessing:
//   - a lead references contacts via lead.contactId + lead.coClients[].contactId
//   - a deal references them via deal.customers[] ({contactId, clientName},
//     or a bare id string on legacy records)

import { isCountableDeal } from "./dealCredit";
import { listClients } from "./leadClients";

export const SEGMENTS = [
  { id: "all", label: "All" },
  { id: "client", label: "Clients" },
  { id: "past", label: "Past clients" },
  { id: "lead", label: "Leads" },
  { id: "other", label: "Everyone else" },
];

const isClosed = (deal) =>
  String(deal?.dealStage || "").toLowerCase() === "closed";

// A timestamp can arrive as epoch ms (createdat) or a date string
// (closingdate, nextFollowUp). Normalize to ms; 0 means "nothing usable".
export function toMs(value) {
  if (!value) return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? 0 : t;
}

export function dealContactIds(deal) {
  const list = Array.isArray(deal?.customers) ? deal.customers : [];
  return list
    .map((c) => (typeof c === "string" ? c : c && c.contactId))
    .filter(Boolean);
}

export function leadContactIds(lead) {
  return listClients(lead)
    .map((c) => c.contactId)
    .filter(Boolean);
}

// contactId -> { deals: [...], leads: [...] }
export function buildContactIndex({
  deals = [],
  buyerLeads = [],
  listingLeads = [],
} = {}) {
  const index = new Map();
  const entryFor = (id) => {
    if (!index.has(id)) index.set(id, { deals: [], leads: [] });
    return index.get(id);
  };

  (deals || []).forEach((deal) => {
    dealContactIds(deal).forEach((id) => entryFor(id).deals.push(deal));
  });
  [...(buyerLeads || []), ...(listingLeads || [])].forEach((lead) => {
    leadContactIds(lead).forEach((id) => entryFor(id).leads.push(lead));
  });

  return index;
}

// A live deal wins over a closed one, and any deal wins over a lead — someone
// mid-transaction is a client even if they also sit in a pipeline.
export function classifySegment(entry) {
  const deals = (entry && entry.deals) || [];
  const leads = (entry && entry.leads) || [];
  const live = deals.filter((d) => isCountableDeal(d) && !isClosed(d));
  if (live.length > 0) return "client";
  if (deals.some(isClosed)) return "past";
  if (leads.length > 0) return "lead";
  return "other";
}

// The most recent thing on record for this person, across their deals and leads.
export function lastActivityAt(entry) {
  const stamps = [];
  ((entry && entry.deals) || []).forEach((d) => {
    stamps.push(toMs(d.closingdate), toMs(d.createdat), toMs(d.updatedAt));
  });
  ((entry && entry.leads) || []).forEach((l) => {
    stamps.push(
      toMs(l.nextFollowUp),
      toMs(l.updatedAt),
      toMs(l.createdAt),
      toMs(l.createdat),
    );
  });
  return stamps.length ? Math.max(...stamps) : 0;
}

// The single most relevant record to link to from a contact row.
export function primaryLink(entry) {
  const deals = (entry && entry.deals) || [];
  const leads = (entry && entry.leads) || [];
  const live = deals.filter((d) => isCountableDeal(d) && !isClosed(d));
  const deal = live[0] || deals.filter(isClosed)[0] || deals[0];
  if (deal) {
    return {
      type: "deal",
      id: deal.id,
      label: deal.propertyaddress || "Deal",
      stage: deal.dealStage || "",
    };
  }
  const lead = leads[0];
  if (lead) {
    return {
      type: "lead",
      id: lead.id,
      label: lead.propertyaddress || lead.contactName || "Lead",
      stage: lead.status || lead.stage || "",
    };
  }
  return null;
}

export function enrichContacts(contacts, data) {
  const index = buildContactIndex(data);
  return (contacts || []).map((contact) => {
    const entry = index.get(contact.id);
    return {
      ...contact,
      segment: classifySegment(entry),
      matchedDeals: (entry && entry.deals) || [],
      matchedLeads: (entry && entry.leads) || [],
      lastActivityAt: lastActivityAt(entry),
      link: primaryLink(entry),
    };
  });
}

export function countBySegment(enriched) {
  const counts = { all: (enriched || []).length, client: 0, past: 0, lead: 0, other: 0 };
  (enriched || []).forEach((c) => {
    if (counts[c.segment] != null) counts[c.segment] += 1;
  });
  return counts;
}

export function filterBySegment(enriched, segment) {
  if (!segment || segment === "all") return enriched || [];
  return (enriched || []).filter((c) => c.segment === segment);
}
